import { z } from 'zod';
import type { SiteToken } from '@sleed/bizcommerce-api';

/**
 * Site edit form. Mirrors `SiteToken`. Only the safely-editable scalar fields are exposed
 * (name, append-language-prefix, visible, sort); the code is read-only and the crawl-state /
 * default-language / default-currency values are server/derived and carried through untouched
 * so an update never corrupts them (legacy onDataGridRowUpdatingMergeUnchanged).
 */
export const siteSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	alp: z.boolean(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	// Carried-through (not edited here):
	cd: z.string().nullable(),
	cs: z.number().nullable(),
	cslst: z.array(z.number()).nullable(),
	cstx: z.string().nullable(),
	odlp: z.boolean().nullable(),
	auesl: z.boolean().nullable(),
	dlnm: z.string().nullable(),
	dcnm: z.string().nullable()
});

export type SiteFormValues = z.infer<typeof siteSchema>;

export const EMPTY_SITE: SiteFormValues = {
	nm: '',
	alp: false,
	iv: true,
	si: 0,
	cd: null,
	cs: null,
	cslst: null,
	cstx: null,
	odlp: null,
	auesl: null,
	dlnm: null,
	dcnm: null
};

export function tokenToForm(token: SiteToken): SiteFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		alp: token.alp ?? false,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		cd: token.cd ?? null,
		cs: token.cs ?? null,
		cslst: token.cslst ?? null,
		cstx: token.cstx ?? null,
		odlp: token.odlp ?? null,
		auesl: token.auesl ?? null,
		dlnm: token.dlnm ?? null,
		dcnm: token.dcnm ?? null
	};
}

export function formToToken(form: SiteFormValues): SiteToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		alp: form.alp,
		iv: form.iv,
		si: form.si,
		cd: form.cd ?? undefined,
		cs: form.cs ?? undefined,
		cslst: form.cslst ?? undefined,
		cstx: form.cstx,
		odlp: form.odlp ?? undefined,
		auesl: form.auesl,
		dlnm: form.dlnm,
		dcnm: form.dcnm
	};
}
